import { rem } from "@certn/cascadia";
import {
  CascadiaColor,
  CascadiaCustomIcon,
  CascadiaMiniIcon,
  CascadiaSpacing,
  CascadiaStandardIcon,
} from "@certn/cascadia-tokens";
import { css } from "@emotion/css";
import classNames from "classnames";

import { customIconComponents } from "./constants";
import { getIcon } from "./utils";
import { useColors } from "../../../hooks/useColors";

import "./Icon.css";

interface BaseProps {
  size?: CascadiaSpacing;
  color?: CascadiaColor;
  padding?: CascadiaSpacing;
  customWidth?: number;
  customHeight?: number;
  testID?: string;
  fill?: boolean;
  fillOverride?: boolean;
  className?: string;
}

export type IconProps = BaseProps & {
  type?: "mini" | "standard" | "custom" | undefined;
  name: CascadiaCustomIcon | CascadiaStandardIcon | CascadiaMiniIcon;
};

export function Icon({
  name,
  type = "standard",
  size = type === "mini" ? "space-4" : "space-5",
  customWidth,
  customHeight,
  color = "icon-base",
  padding,
  testID = "icon",
  fill,
  fillOverride,
  className,
}: IconProps) {
  const icon = getIcon(type, name);
  const label = `${name.replaceAll(`[_-]`, " ")} icon`;
  const colors = useColors();

  if (icon?.lib === "custom") {
    return (
      <i
        role="img"
        aria-label={label}
        className={css([
          {
            display: "inline-block",
            width: customWidth ? customWidth : rem(size),
            height: customHeight ? customHeight : rem(size),
            fill: color ? colors[color] : "var(--icon-base)",
            overflow: "hidden",
            "> svg": css({
              display: "block",
              width: "100%",
              height: "100%",
            }),
            aspectRatio: 1,
          },
          className,
        ])}
        data-testid={testID}
      >
        {customIconComponents[name as CascadiaCustomIcon]}
      </i>
    );
  }

  return (
    <i
      role="img"
      aria-label={label}
      style={{ fontSize: rem(size) }}
      className={classNames(
        "material-symbols-rounded",
        css(
          type === "mini" || fill ? styles.fill : styles.standard,
          fillOverride && styles.standard,
          {
            aspectRatio: 1 / 1,
            color: color ? colors[color] : undefined,
            padding: padding ? rem(padding) : undefined,
          }
        ),
        className
      )}
      data-testid={testID}
    >
      {icon?.name}
    </i>
  );
}

const styles = {
  fill: css({
    fontVariationSettings: '"FILL" 1, "wght" 400, "GRAD" 200, "opsz" 20',
  }),
  standard: css({
    fontVariationSettings: '"FILL" 0, "wght" 400, "GRAD" 0, "opsz" 24',
  }),
};
